import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import clsx from 'clsx';
import { removeDuplicateNodes } from '@helpers/utils';

import SEO from '@common/SEO';
import Main from '@common/Main';
import Section from '@common/Section';
import Container from '@common/Container';
import Row from '@common/Row';
import Heading from '@common/Heading';
import RichText from '@common/RichText';
import Button from '@common/Button';
import Img from '@common/Image';
import Video from '@common/Video';
import Link from '@common/Link';
import SmallTitle from '@common/SmallTitle';

import ColouredTextBlock from '@common/ColouredTextBlock';

import CenterSize from '@images/customer-stories/center_size.svg';
import Location from '@images/customer-stories/location.svg';
import BusinessSize from '@images/customer-stories/business_size.svg';

// Feature icons
import ActivityPlanning from '@images/customer-stories/activity_planning.svg';
import BillingPayments from '@images/customer-stories/billing_payments.svg';
import CenterManagement from '@images/customer-stories/center_management.svg';
import DigitalCurriculum from '@images/customer-stories/digital_curriculum.svg';
import FamilyEngagement from '@images/customer-stories/family_engagement.svg';
import PhysicalCurriculum from '@images/customer-stories/physical_curriculum.svg';
import ProfDevelopment from '@images/customer-stories/prof_development.svg';

const FeatureIcons = {
  'Activity Planning': ActivityPlanning,
  'Billing & Payments': BillingPayments,
  'Center Management': CenterManagement,
  'Digital Curriculum': DigitalCurriculum,
  'Family Engagement': FamilyEngagement,
  'Physical Curriculum': PhysicalCurriculum,
  'Professional Development': ProfDevelopment
};

const IconColours = [
  'teal',
  'yellow',
  'green',
  'red',
  'teal',
  'yellow',
  'green'
];

const sidebarColours = [
  'teal',
  'yellow',
  'red',
  'green'
];

export const query = graphql`
  query($slug: String!, $title: String!) {
    contentfulCustomerStory(slug: { eq: $slug }) {
      ...CustomerStoryFragment
    }
    allContentfulCustomerStory(
      filter: {
        title: { ne: $title}
      }
      limit: 2
    ) {
      edges {
        node {
          id
          title
          slug
          customer {
            ...User
          }
          image {
            ...Image
          }
        }
      }
    }
  }
`

const CustomerStory = ({ data }) => {
  const {
    title,
    video,
    image,
    mainContent,
    sidebar,
    customer
  } = data?.contentfulCustomerStory || {};
  const [relatedStories, setRelatedStories] = useState([]);
  useEffect(() => {    
    // Since we have two contentfuls, nodes can be duplicated
    const newList = removeDuplicateNodes(data?.allContentfulCustomerStory.edges, 'title');
    setRelatedStories(newList);
  },[data]);
  return (
    <Main>
      <Section spacing='mt-4'>
        <Container>
          {customer?.company &&
            <div className='fw-semibold text-uppercase text-blue mb-4'>
              <Link to='/resources/customer-stories'>{'Customers'}</Link>
              <span className='ms-2 text-black'>{`/${customer.company}`}</span>
            </div>
          }
          <Row>
            <div className={clsx('col col-xl-7 pe-3 mb-3 mb-xl-0', {'border-bottom border-2':false})}>
              {title && <Heading type='h1' className='fs-36 mb-3'>{title}</Heading>}
              {(!video && image) &&
                <Img
                  loading='eager'
                  image={image.gatsbyImageData}
                  file={image.file}
                  alt={image.description}
                />
              }
              {video &&
                <Video videoId={video.videoId} image={video.image}/>
              }
              {customer?.features &&
                <div className='pb-5 mt-3 mb-5'>
                  <SmallTitle>{'Products used'}</SmallTitle>
                  <ul className='row row-cols-sm-2 gy-4 ps-0'>
                    {customer.features.map((feature, index) => (
                      <li key={index} className='d-flex justify-content-start align-items-center'>
                        <div style={{minHeight: 63, minWidth: 63}} className={clsx('d-flex rounded-circle justify-content-center align-items-center', [`bg-${IconColours[index]}`])}>
                          <img height={44} width={44} src={FeatureIcons[feature]} alt=''  />
                        </div>
                        <span className='ms-1 fs-24'>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              }
              {mainContent && mainContent.map((data, index) => {
                if (data.__typename === 'ContentfulTextBlock') {
                  return (
                    <div key={index} className='mb-5'>
                      {(data.showTitle && data.title) && <SmallTitle>{data.title}</SmallTitle>}
                      {data.text && <RichText h2='fs-24 fw-extrabold mb-3' json={data.text}/>}
                    </div>
                  )
                }
                else if (data.__typename === 'ContentfulFlexBlock') {
                  return (
                    <div key={index} className='mb-5'>
                      {(data.showTitle && data.title) && <Heading type='h2' className='fs-24 mb-3'>{data.title}</Heading>}
                      {data.text && <RichText json={data.text}/>}
                      {data.buttons &&
                        <div className='d-flex flex-column flex-sm-row'>
                          {data.buttons.map(button => (
                            <Button
                              key={button.id}
                              to={button.link}
                              variant={button.variant}
                              size={button.size}
                              addClass={clsx('scale-1 mb-2', button.class)}
                              copy={button.copy}
                              icon={button.icon}
                              iconSize={button.iconSize}
                              iconPosition={button.iconPosition}
                              modal={button.modal}
                            />
                          ))}
                        </div>
                      }
                    </div>
                  )
                }
              })}
            </div>
            <div className='col-xl-4'>
              {customer &&
                <div className={clsx('bg-light-grey-2 w-100 rounded p-3', {'pb-7':customer})}>
                  {(!customer?.centerLogo && customer?.company) &&
                    <p className='mb-3'>{customer.company}</p>
                  }
                  {customer?.centerLogo &&
                    <Img
                      loading='eager'
                      image={customer.centerLogo.gatsbyImageData}
                      file={customer.centerLogo.file}
                      alt={customer.centerLogo.description}
                      className='border-bottom mb-3'
                    />
                  }
                  {(customer.centerSize || customer.location || customer.businessSize) &&
                    <div className='border-bottom py-1 mb-3'>
                      {customer?.centerSize &&
                        <div className='d-flex align-items-center mb-1'>
                          <img height={20} width={20} src={CenterSize} alt='' />
                          <div className='ms-1'>{customer.centerSize}</div>
                        </div>
                      }
                      {customer?.location &&
                        <div className='d-flex align-items-center mb-1'>
                          <img height={20} width={20} src={Location} alt='' />
                          <div className='ms-1'>{customer.location}</div>
                        </div>
                      }
                      {customer?.businessSize &&
                        <div className='d-flex align-items-center'>
                          <img height={20} width={20} src={BusinessSize} alt='' />
                          <div className='ms-1'>{customer.businessSize}</div>
                        </div>
                      }
                    </div>
                  }
                  {customer.centerBackground &&
                    <div className='border-bottom mb-3 fs-16'>
                      <SmallTitle>{'Center Background'}</SmallTitle>
                      <RichText json={customer.centerBackground}/>
                    </div>
                  }
                  {customer.educatorBio &&
                    <div className='border-bottom mb-3 fs-16'>
                      <SmallTitle>{'Educator Bio'}</SmallTitle>
                      <RichText json={customer.educatorBio}/>
                    </div>
                  }
                  {customer &&
                    <Row>
                      {customer.image &&
                        <div className='col-auto pe-0'>
                          <Img
                            loading='eager'
                            image={customer.image.gatsbyImageData}
                            file={customer.image.file}
                            alt={customer.image.description}
                            style={{minWidth: '64px', minHeight: '64px', maxWidth: '64px', maxHeight: '64px', borderRadius: '50%', border: '1px solid #707070'}}
                          />
                        </div>
                      }
                      <div className='col'>
                        {customer.name && <p className='text-uppercase fw-semibold'>{customer.name}</p>}
                        {customer.title && <p className='text-uppercase'>{customer.title}</p>}
                      </div>
                    </Row>
                  }
                </div>
              }
              {sidebar &&
                <div className='mt-5 list-title-border'>
                  {sidebar.map((data, index) => {
                    if (data.__typename === 'ContentfulTextBlock') {
                      return (
                        <div key={index} className='mb-3'>
                          <ColouredTextBlock key={index} data={data} colour={sidebarColours[index]}/>
                        </div>
                      )
                    }
                    else if (data.__typename === 'ContentfulFlexBlock') {
                      return (
                        <div key={index} className='mb-3'>
                          {(data.showTitle && data.title) && <Heading type='h3' className='fs-20 mb-3'>{data.title}</Heading>}
                          {data.text && <RichText json={data.text}/>}
                          {data.buttons &&
                            <div className='d-flex flex-column flex-sm-row'>
                              {data.buttons.map(button => (
                                <Button
                                  key={button.id}
                                  to={button.link}
                                  variant={button.variant}
                                  size={button.size}
                                  addClass={clsx('scale-1 mb-2', button.class)}
                                  copy={button.copy}
                                  icon={button.icon}
                                  iconSize={button.iconSize}
                                  iconPosition={button.iconPosition}
                                  modal={button.modal}
                                />
                              ))}
                            </div>
                          }
                        </div>
                      )
                    }
                  })}
                </div>
              }
            </div>
          </Row>
          {relatedStories.length > 0 &&
            <>
              <Heading type='h2' className='fs-24 mt-3 mb-3'>{'Related Case Studies'}</Heading>
              <Row className='row-cols-3'>
                {relatedStories.map((story, index) => (
                  <Link key={index} to={`/resources/customer-stories${story.node.slug}`} className='scale-1'>
                    <div className='position-relative mb-3 rounded'>
                      {story.node.image &&
                        <Img
                          loading='eager'
                          image={story.node.image.gatsbyImageData}
                          file={story.node.image.file}
                          alt={story.node.image.description}
                        />
                      }
                    </div>
                    {story.node.title && <div className='fs-16 text-black mb-3'>{story.node.title}</div>}
                  </Link>
                ))}
              </Row>
            </>
          }
        </Container>
      </Section>
    </Main>
  )
}

export default CustomerStory

export const Head = ({ location, data }) => {
  const {
    index,
    title,
    image,
    customer
  } = data?.contentfulCustomerStory || {};
  return (
    <SEO
      index={index}
      titleTag={`${customer?.company} | Customer Stories`}
      metaDescription={title}
      metaImage={image}
      location={location}
    />
  )
}
